import type { FC, RefAttributes } from "react";
import { cn } from "@/lib/utils";
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";

const Table: FC<HTMLAttributes<HTMLTableElement> & RefAttributes<HTMLTableElement>> = ({
  ref,
  className,
  ...props
}) => <table rules="rows" border={1} ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />;
Table.displayName = "Table";

const TableHeader: FC<HTMLAttributes<HTMLTableSectionElement> & RefAttributes<HTMLTableSectionElement>> = ({
  ref,
  className,
  ...props
}) => <thead ref={ref} className={cn("bg-background rounded-md [&_tr]:border-b", className)} {...props} />;
TableHeader.displayName = "TableHeader";

const TableBody: FC<HTMLAttributes<HTMLTableSectionElement> & RefAttributes<HTMLTableSectionElement>> = ({
  ref,
  className,
  ...props
}) => <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
TableBody.displayName = "TableBody";

const TableFooter: FC<HTMLAttributes<HTMLTableSectionElement> & RefAttributes<HTMLTableSectionElement>> = ({
  ref,
  className,
  ...props
}) => <tfoot ref={ref} className={cn("bg-primary font-medium text-primary-foreground", className)} {...props} />;
TableFooter.displayName = "TableFooter";

const TableRow: FC<HTMLAttributes<HTMLTableRowElement> & RefAttributes<HTMLTableRowElement>> = ({
  ref,
  className,
  ...props
}) => (
  <tr ref={ref} className={cn("border-b hover:bg-muted/50 data-[state=selected]:bg-muted", className)} {...props} />
);
TableRow.displayName = "TableRow";

const TableHead: FC<ThHTMLAttributes<HTMLTableCellElement> & RefAttributes<HTMLTableCellElement>> = ({
  ref,
  className,
  ...props
}) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const TableCell: FC<TdHTMLAttributes<HTMLTableCellElement> & RefAttributes<HTMLTableCellElement>> = ({
  ref,
  className,
  ...props
}) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle text-foreground [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
);
TableCell.displayName = "TableCell";

const TableCaption: FC<HTMLAttributes<HTMLTableCaptionElement> & RefAttributes<HTMLTableCaptionElement>> = ({
  ref,
  className,
  ...props
}) => <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />;
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
